import React, { useEffect, useRef, useState } from 'react';
import { getApiUrl } from '../../config';
import { useAuth } from '../../contexts/AuthContext';

interface LiveOutputProps {
  recipeId?: number;
  focusRequest?: number;
}

const MAX_OUTPUT_LINES = 500;
const MAX_OUTPUT_CHARACTERS = 500_000;

type OutputEntry = {
  at?: string;
  text: string;
  type?: string;
};

function timestamp(value?: string): string {
  if (!value) return '---- -- -- --:--:--';
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return '---- -- -- --:--:--';
  const pad = (part: number) => String(part).padStart(2, '0');
  return `${date.getFullYear()}-${pad(date.getMonth() + 1)}-${pad(date.getDate())} ${pad(date.getHours())}:${pad(date.getMinutes())}:${pad(date.getSeconds())}`;
}

function compact(text: string): string {
  return text.replace(/\s+/g, ' ').trim();
}

const ENTRY_PRESENTATION: Record<string, { label: string; className: string }> = {
  status: { label: 'Status', className: 'text-sky-300' },
  progress: { label: 'Progress', className: 'text-gray-300' },
  result: { label: 'Result', className: 'text-emerald-300' },
  warning: { label: 'Warning', className: 'text-amber-300' },
  error: { label: 'Error', className: 'text-red-300' },
  tool: { label: 'Detail', className: 'text-violet-300' },
};

const LiveOutput: React.FC<LiveOutputProps> = ({ recipeId, focusRequest = 0 }) => {
  const { token } = useAuth();
  const [entries, setEntries] = useState<OutputEntry[]>([]);
  const [selectedEntry, setSelectedEntry] = useState<OutputEntry | null>(null);
  const [status, setStatus] = useState<'connecting' | 'connected' | 'disconnected'>('connecting');
  const consoleRef = useRef<HTMLDivElement>(null);
  const followOutputRef = useRef(true);

  useEffect(() => {
    setEntries([]);
    setSelectedEntry(null);
    followOutputRef.current = true;
    if (!recipeId || !token) return;

    const controller = new AbortController();
    let reconnectTimer: ReturnType<typeof setTimeout> | undefined;

    const connect = async () => {
      setStatus('connecting');
      try {
        const response = await fetch(getApiUrl(`recipes/${recipeId}/live-output`), {
          headers: { Authorization: `Bearer ${token}`, Accept: 'text/event-stream' },
          signal: controller.signal
        });
        if (!response.ok || !response.body) throw new Error(`Live output connection failed (${response.status})`);

        setStatus('connected');
        const reader = response.body.getReader();
        const decoder = new TextDecoder();
        let buffer = '';

        while (true) {
          const { done, value } = await reader.read();
          if (done) break;
          buffer += decoder.decode(value, { stream: true }).replace(/\r\n/g, '\n');
          let boundary: number;
          while ((boundary = buffer.indexOf('\n\n')) !== -1) {
            const message = buffer.slice(0, boundary);
            buffer = buffer.slice(boundary + 2);
            const event = message.split('\n').find(line => line.startsWith('event:'))?.slice(6).trim();
            const data = message.split('\n').find(line => line.startsWith('data:'))?.slice(5).trim();
            if ((event === 'output' || event === 'snapshot') && data) {
              const payload = JSON.parse(data) as OutputEntry;
              if (event === 'snapshot') {
                setEntries(payload.text ? [{ ...payload, text: payload.text.slice(-MAX_OUTPUT_CHARACTERS) }] : []);
              } else {
                setEntries(previous => {
                  const next = [...previous, payload].slice(-MAX_OUTPUT_LINES);
                  let characters = next.reduce((total, entry) => total + entry.text.length, 0);
                  while (next.length > 1 && characters > MAX_OUTPUT_CHARACTERS) {
                    characters -= next[0].text.length;
                    next.shift();
                  }
                  return next;
                });
              }
            }
          }
        }
        throw new Error('Live output connection closed');
      } catch (error) {
        if (controller.signal.aborted) return;
        console.error('Live output stream error:', error);
        setStatus('disconnected');
        reconnectTimer = setTimeout(connect, 2000);
      }
    };

    void connect();
    return () => {
      controller.abort();
      if (reconnectTimer) clearTimeout(reconnectTimer);
    };
  }, [recipeId, token]);

  useEffect(() => {
    if (followOutputRef.current && consoleRef.current) {
      consoleRef.current.scrollTop = consoleRef.current.scrollHeight;
    }
  }, [entries]);

  useEffect(() => {
    if (!focusRequest) return;
    const frame = window.requestAnimationFrame(() => {
      const consoleElement = consoleRef.current;
      if (consoleElement?.getClientRects().length) consoleElement.focus();
    });
    return () => window.cancelAnimationFrame(frame);
  }, [focusRequest]);

  useEffect(() => {
    if (!selectedEntry) return;
    const closeOnEscape = (event: KeyboardEvent) => {
      if (event.key === 'Escape') setSelectedEntry(null);
    };
    window.addEventListener('keydown', closeOnEscape);
    return () => window.removeEventListener('keydown', closeOnEscape);
  }, [selectedEntry]);

  return (
    <>
      <div
        ref={consoleRef}
        dir="ltr"
        onScroll={() => {
          const element = consoleRef.current;
          if (element) followOutputRef.current = element.scrollHeight - element.scrollTop - element.clientHeight < 48;
        }}
        className="h-full overflow-auto bg-gray-950 p-3 font-mono text-sm leading-5 text-gray-100"
        tabIndex={-1}
        aria-live="polite"
        aria-label="Recipe live output"
      >
        {entries.length ? (
          <div className="space-y-0.5">
            {entries.map((entry, index) => {
              const presentation = ENTRY_PRESENTATION[entry.type || ''] || { label: 'Output', className: 'text-gray-300' };
              return (
                <button
                  key={`${entry.at || 'output'}-${index}`}
                  type="button"
                  onClick={() => setSelectedEntry(entry)}
                  className="flex w-full gap-3 rounded px-1 py-0.5 text-left hover:bg-gray-800 focus:bg-gray-800 focus:outline-none"
                  title="Show full log entry"
                >
                  <span className="shrink-0 text-gray-500">[{timestamp(entry.at)}]</span>
                  <span className={`w-16 shrink-0 ${presentation.className}`}>{presentation.label}</span>
                  <span className="min-w-0 truncate text-gray-100">{compact(entry.text)}</span>
                </button>
              );
            })}
          </div>
        ) : (
          <div className="flex h-full items-center justify-center text-center text-gray-500">
            No output yet. Output will appear here when the recipe runs.
          </div>
        )}
        {status === 'disconnected' && (
          <div className="sticky bottom-0 mt-2 bg-gray-950/90 py-1 text-xs text-amber-400">
            Live connection interrupted. Reconnecting…
          </div>
        )}
      </div>

      {selectedEntry && (
        <div
          className="fixed inset-0 z-[10000] flex items-center justify-center bg-black/70 p-4"
          role="dialog"
          aria-modal="true"
          aria-label="Full log entry"
          onClick={() => setSelectedEntry(null)}
        >
          <div
            className="flex max-h-[85vh] w-full max-w-4xl flex-col overflow-hidden rounded-lg border border-gray-700 bg-gray-950 shadow-2xl"
            onClick={event => event.stopPropagation()}
          >
            <div className="flex items-center justify-between border-b border-gray-800 px-4 py-3 font-mono text-sm text-gray-400">
              <span>[{timestamp(selectedEntry.at)}]{selectedEntry.type ? ` ${selectedEntry.type}` : ''}</span>
              <button type="button" onClick={() => setSelectedEntry(null)} className="rounded px-2 py-1 text-gray-300 hover:bg-gray-800 hover:text-white">Close</button>
            </div>
            <pre dir="ltr" className="m-0 overflow-auto whitespace-pre-wrap break-words p-4 text-left font-mono text-sm leading-5 text-gray-100 [unicode-bidi:isolate]">{selectedEntry.text}</pre>
          </div>
        </div>
      )}
    </>
  );
};

export default LiveOutput;
